// useNotificationActions — turns the user's UNREAD notifications into HomeActions
// so "something happened → attend to it" surfaces inside the guided-Home stack.
//
// Grouped by SEMANTIC KEY (derived from each notification's action_url), so:
//   - N notifications of the same concern collapse into ONE card
//     ("3 nya inbjudningar") instead of N near-identical cards;
//   - the key lets the Home drop a notification group when a data card already
//     covers that concern (see HomeAction.dedupeKey) — each concern shows once.
//
// New notification types appear here for free; only the label map + key mapping
// below need a line when a brand-new concern/route is introduced.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ArrowRight } from 'lucide-react';
import type { UserNotificationWithDetails } from 'jasit-api-kit';
import { getNotificationApi } from '@/lib/api/client';
import type { HomeAction } from './HomeActionStack';

const actionUrlOf = (n: UserNotificationWithDetails): string | undefined => {
    const v = n.content?.action_url;
    return typeof v === 'string' && v ? v : undefined;
};

// Map a notification's action_url to a semantic concern key. Keys must match the
// dedupeKeys the Homes put on their data cards (invitation / onboarding /
// workplace / employer-application). Unmapped routes group by route string and
// never dedupe against a data card (they just show — additive).
const keyOf = (url: string | undefined): string => {
    const u = url || '';
    if (u.startsWith('/dashboard/my-invitations')) return 'invitation';
    if (u.startsWith('/dashboard/my-onboarding')) return 'onboarding';
    if (u.startsWith('/dashboard/my-workplace')) return 'workplace';
    if (u.startsWith('/dashboard/my-applications')) return 'candidate-application';
    if (u.startsWith('/dashboard/needs')) return 'employer-application';
    if (u.startsWith('/dashboard/jobs')) return 'posting';
    return u || 'other';
};

// Plural label for a collapsed group (>1). Singular cards use the notification's
// own title instead. Unknown keys fall back to a generic count.
const GROUP_LABEL: Record<string, (n: number) => string> = {
    invitation: (n) => `${n} nya inbjudningar`,
    onboarding: (n) => `${n} onboarding-uppdateringar`,
    workplace: (n) => `${n} uppdateringar om din anställning`,
    'candidate-application': (n) => `${n} uppdateringar om dina ansökningar`,
    'employer-application': (n) => `${n} nya ansökningar`,
    posting: (n) => `${n} nya matchande tjänster`,
};

export function useNotificationActions(): HomeAction[] {
    const navigate = useNavigate();
    const [items, setItems] = useState<UserNotificationWithDetails[]>([]);

    useEffect(() => {
        let cancelled = false;
        getNotificationApi()
            .getMyNotifications({ page: 1, limit: 30 })
            .then((list) => {
                if (cancelled) return;
                setItems((list ?? []).filter((n) => n.status !== 'read'));
            })
            .catch(() => { /* best-effort — Home is fine without notifications */ });
        return () => { cancelled = true; };
    }, []);

    // Group unread by concern, preserving first-seen (newest-first) order.
    const groups = new Map<string, UserNotificationWithDetails[]>();
    for (const n of items) {
        const k = keyOf(actionUrlOf(n));
        const arr = groups.get(k);
        if (arr) arr.push(n);
        else groups.set(k, [n]);
    }

    const actions: HomeAction[] = [];
    for (const [key, group] of groups) {
        const head = group[0];
        const url = actionUrlOf(head);
        const multi = group.length > 1;
        const label = GROUP_LABEL[key];
        actions.push({
            key: `notif-${key}`,
            when: true,
            dedupeKey: key,
            icon: Bell,
            tone: key === 'workplace' ? 'green' : 'blue',
            title: multi
                ? (label
                    ? label(group.length)
                    // No mapped label: if they share a title (e.g. "Inbjudan
                    // accepterad"), show it with a count; else a generic count.
                    : group.every((g) => g.title === head.title) && head.title
                        ? `${head.title} (${group.length})`
                        : `${group.length} nya aviseringar`)
                : (head.title || 'Ny avisering'),
            description: multi ? undefined : (head.message || undefined),
            ctaLabel: 'Visa',
            ctaIcon: ArrowRight,
            onClick: async () => {
                // Mark the whole group read so the card (and its bell count) clears.
                await Promise.all(group.map((g) => getNotificationApi().markAsRead(g.id).catch(() => {})));
                if (url) navigate(url);
            },
        });
    }
    return actions;
}
